(() => {
  'use strict';
  if (window.__uniAppliedAIReady) return;
  window.__uniAppliedAIReady = true;
  const instances = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  function init(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const on = { signal: abort.signal };
    const motionAllowed = () => !reduced.matches && document.body.dataset.motion !== 'none' && document.body.dataset.motionUser !== 'none' && root.dataset.motion !== 'none';
    const deck = root.querySelector('[data-learning-deck]');
    let resetGesture = () => {};
    if (deck) {
      const stage = deck.querySelector('[data-deck-stage]');
      const cards = [...deck.querySelectorAll('[data-learning-card]')];
      const tabs = [...deck.querySelectorAll('[data-deck-tab]')];
      const nav = deck.querySelector('[data-deck-tabs]');
      let current = 0;
      let gesture = null;
      const select = (index, focus = false) => {
        const moveFocus = focus || cards[current].contains(document.activeElement);
        current = (index + cards.length) % cards.length;
        cards.forEach((card, i) => {
          const active = i === current;
          card.dataset.place = active ? 'active' : i === (current + 1) % cards.length ? 'after' : 'before';
          card.setAttribute('aria-hidden', String(!active));
          card.inert = !active;
          card.tabIndex = active ? 0 : -1;
          tabs[i].setAttribute('aria-selected', String(active));
          tabs[i].tabIndex = active ? 0 : -1;
        });
        if (moveFocus) tabs[current].focus({ preventScroll: true });
      };
      nav.setAttribute('role', 'tablist');
      tabs.forEach((tab, i) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', cards[i].id);
        cards[i].setAttribute('role', 'tabpanel');
        cards[i].setAttribute('aria-labelledby', tab.id);
        tab.addEventListener('click', () => select(i), on);
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (i + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          select(next, true);
        }, on);
      });
      resetGesture = () => {
        const previous = gesture;
        gesture = null;
        if (previous && stage.hasPointerCapture?.(previous.id)) stage.releasePointerCapture(previous.id);
        delete deck.dataset.deckDragging;
        stage.style.removeProperty('--deck-drag');
        stage.style.removeProperty('--deck-tilt');
      };
      stage.addEventListener('dragstart', event => event.preventDefault(), on);
      stage.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0 || event.target.closest('a,button,input,select,textarea')) return;
        gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
        stage.setPointerCapture?.(event.pointerId);
      }, on);
      stage.addEventListener('pointermove', event => {
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 12) { resetGesture(); return; }
        if (Math.abs(dx) < 8 || !motionAllowed()) return;
        deck.dataset.deckDragging = 'true';
        const drag = Math.max(-55, Math.min(55, dx * .3));
        stage.style.setProperty('--deck-drag', `${drag}px`);
        stage.style.setProperty('--deck-tilt', `${drag / 28}deg`);
      }, on);
      stage.addEventListener('pointerup', event => {
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        const threshold = Math.min(50, stage.clientWidth * .12);
        resetGesture();
        if (Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy) * 1.35) select(current + (dx < 0 ? 1 : -1));
      }, on);
      stage.addEventListener('pointercancel', resetGesture, on);
      stage.addEventListener('lostpointercapture', () => { if (gesture) resetGesture(); }, on);
      select(0);
      deck.dataset.deckReady = 'true';
      deck.querySelector('[data-deck-controls]').hidden = false;
    }
    const sync = () => {
      if (deck) deck.dataset.deckMotion = motionAllowed() ? 'full' : 'none';
      if (!motionAllowed()) resetGesture();
    };
    sync();
    document.addEventListener('uni:motionchange', sync, on);
    reduced.addEventListener('change', sync, on);
    const preferences = new MutationObserver(sync);
    preferences.observe(root, { attributes: true, attributeFilter: ['data-motion'] });
    preferences.observe(document.body, { attributes: true, attributeFilter: ['data-motion', 'data-motion-user'] });
    instances.set(root, () => { resetGesture(); abort.abort(); preferences.disconnect(); });
  }
  const within = scope => {
    if (scope.matches?.('uni-program[data-distance-experience="true"]')) init(scope);
    scope.querySelectorAll?.('uni-program[data-distance-experience="true"]').forEach(init);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => within(document), { once: true });
  else within(document);
  document.addEventListener('shopify:section:load', event => within(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, dispose] of instances) if (!root.isConnected || event.target === root || event.target.contains(root)) { dispose(); instances.delete(root); }
  });
})();
